import {useMemo} from 'react';
import {useDispatch} from 'react-redux';
import type {Sorting} from '@igp/shared/api';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import type {ReferralLinkConfig} from '@igp/shared/contract/pam';
import type {LicenseType, ServerLicenseType} from '@igp/shared/features/brand-license';
import {LicenseTypeTypeName, ServerLicenseTypeTypeName} from '@igp/shared/features/brand-license';
import {useAsyncAction} from '@igp/shared/redux/async-actions';
import {useReduxForm, type UseReduxFormReturn} from '@igp/shared/redux/form';
import {RealtimeMessageTrigger} from '@igp/shared/redux/realtime';
import {extendedViewCleanDelay, type UseListViewEntityProps, type UseListViewEntityResult, useViewInit} from '@igp/shared/redux/view';

import {referralLinkConfigActions} from './actions';
import {
    referralLinkConfigEntityType,
    ReferralLinkConfigQueryFieldsTypeName,
    ReferralLinkConfigSortKeysTypeName,
    ReferralLinkConfigTypeName,
    ReferralLinkConfigViewModelKeysTypeName,
    ReferralLinkConfigViewModelTypeName,
} from './constants';
import type {
    EditReferralLinkConfigPayload,
    ReferralLinkConfigEntityType,
    ReferralLinkConfigFilterKeys,
    ReferralLinkConfigQueryFields,
    ReferralLinkConfigSortKeys,
    ReferralLinkConfigViewModel,
    ReferralLinkConfigViewModelKeys,
} from './types';

export function useReferralLinkConfigs({
    viewType,
    fields,
    defaultSorting,
    defaultFilters,
    defaultPaging,
    syncWithUrl,
    validateFilter,
    cleanDelay = extendedViewCleanDelay,
    displayName,
    realtimeMode,
    isCancelable,
}: UseListViewEntityProps<ReferralLinkConfigFilterKeys, ReferralLinkConfigViewModelKeys>): UseListViewEntityResult<
    ReferralLinkConfigViewModel,
    ReferralLinkConfigViewModelKeys
> {
    const mapper = useAutoMapper();
    const queryFields = fields.map(f =>
        mapper.map<ReferralLinkConfigViewModelKeys, ReferralLinkConfigQueryFields>(
            f,
            ReferralLinkConfigViewModelKeysTypeName,
            ReferralLinkConfigQueryFieldsTypeName
        )
    );

    function mapSorting(sorting: Sorting<ReferralLinkConfigViewModelKeys>[] = []): Sorting<ReferralLinkConfigSortKeys>[] {
        return sorting.map(s => ({
            field: mapper.map<ReferralLinkConfigViewModelKeys, ReferralLinkConfigSortKeys>(
                s.field,
                ReferralLinkConfigViewModelKeysTypeName,
                ReferralLinkConfigSortKeysTypeName
            ),
            sort: s.sort,
        }));
    }

    const {
        items: serverItems,
        totalCount,
        viewEntity,
        getSearchFilter,
        handleSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    } = useViewInit<
        ReferralLinkConfigEntityType,
        ReferralLinkConfig,
        ReferralLinkConfigFilterKeys,
        ReferralLinkConfigQueryFields,
        ReferralLinkConfigSortKeys
    >({
        viewType,
        entity: {entity: referralLinkConfigEntityType, fields: queryFields},
        cleanDelay,
        defaultFilters,
        defaultPaging,
        defaultSorting: mapSorting(defaultSorting),
        syncWithUrl,
        validateFilter,
        displayName,
        realtime: realtimeMode
            ? {
                  entity: referralLinkConfigEntityType,
                  mode: realtimeMode,
                  triggers: [
                      {type: RealtimeMessageTrigger.Add},
                      {type: RealtimeMessageTrigger.Update},
                      {type: RealtimeMessageTrigger.Delete},
                  ],
              }
            : null,
        isCancelable,
    });

    const items = serverItems.map(item =>
        mapper.tryMap<ReferralLinkConfig, ReferralLinkConfigViewModel>(
            item,
            ReferralLinkConfigTypeName,
            ReferralLinkConfigViewModelTypeName
        )
    );

    const searchFilter = getSearchFilter(sortField =>
        mapper.map<ReferralLinkConfigSortKeys, ReferralLinkConfigViewModelKeys>(
            sortField,
            ReferralLinkConfigSortKeysTypeName,
            ReferralLinkConfigViewModelKeysTypeName
        )
    );

    function handleVmSortChange(sorting: Sorting<ReferralLinkConfigViewModelKeys>[]): void {
        handleSortChange(mapSorting(sorting));
    }

    return {
        items: items?.map(i => i.model),
        rawItems: items?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        totalCount,
        searchFilter,
        filterString: viewEntity?.filter,
        handleSortChange: handleVmSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    };
}

type UseReferralLinkConfigRemoveResult = {
    handleRemove: (aalGroupName: string, license?: ServerLicenseType) => void;
};

export function useReferralLinkConfigRemove(): UseReferralLinkConfigRemoveResult {
    const dispatch = useDispatch();

    function handleRemove(aalGroupName: string, license?: ServerLicenseType): void {
        dispatch(
            referralLinkConfigActions.removeReferralLinkConfig.request(
                {aal_group_name: aalGroupName, license},
                {actionId: `${aalGroupName}_${license ?? ''}`}
            )
        );
    }

    return {handleRemove};
}

type ReferralLinkConfigFormViewModel = {
    aal_group_name: string;
    referral_link_base_url: string;
    license?: LicenseType;
};
type UseReferralLinkConfigurationEditProps = {
    initialModel?: ReferralLinkConfigViewModel | null;
    onSuccess?: (submittedValue: ReferralLinkConfigFormViewModel) => void;
    isEditMode: boolean;
};

type UseReferralLinkConfigurationEditResult = UseReduxFormReturn<ReferralLinkConfigFormViewModel>;

export function useReferralLinkConfigurationEdit({
    initialModel,
    onSuccess,
    isEditMode,
}: UseReferralLinkConfigurationEditProps): UseReferralLinkConfigurationEditResult {
    const mapper = useAutoMapper();

    const defaultModel: ReferralLinkConfigFormViewModel = initialModel
        ? {
              aal_group_name: initialModel.aal_group_name,
              referral_link_base_url: initialModel.referral_link_base_url ?? '',
              license: mapper.map<ServerLicenseType, LicenseType>(initialModel.license, ServerLicenseTypeTypeName, LicenseTypeTypeName),
          }
        : {aal_group_name: '', referral_link_base_url: ''};
    const asyncAction = isEditMode ? referralLinkConfigActions.editReferralLinkConfig : referralLinkConfigActions.addReferralLinkConfig;

    const formMethods = useReduxForm<ReferralLinkConfigFormViewModel, EditReferralLinkConfigPayload>({
        initialModel: defaultModel,
        asyncAction,
        map: model => ({
            aal_group_name: model.aal_group_name,
            referral_link_base_url: model.referral_link_base_url?.trim() ?? '',
            license: mapper.map<LicenseType, ServerLicenseType>(model.license, LicenseTypeTypeName, ServerLicenseTypeTypeName),
        }),
        onSuccess,
        mode: 'onChange',
        modelId: initialModel?.aal_group_name,
    });

    return formMethods;
}

type UseReferralLinkConfigStatusesResult = {
    isAddInProgress: boolean;
    inProgressIds: string[];
};

export function useReferralLinkConfigActionStatuses(ids: string[] = []): UseReferralLinkConfigStatusesResult {
    const {noIdActionState: addActionState} = useAsyncAction({asyncAction: referralLinkConfigActions.addReferralLinkConfig});
    const {idActionState: removeActionState} = useAsyncAction({asyncAction: referralLinkConfigActions.removeReferralLinkConfig});
    const {idActionState: editActionState} = useAsyncAction({asyncAction: referralLinkConfigActions.editReferralLinkConfig});

    const editInProgressIds =
        ids
            .map(id => editActionState[id])
            .filter(state => state?.isProgress)
            .map(state => state?.actionId) ?? [];
    const removeInProgressIds =
        ids
            .map(id => removeActionState[id])
            .filter(state => state?.isProgress)
            .map(state => state?.actionId) ?? [];

    const inProgressIds = useMemo(() => {
        return Array.from(new Set([...editInProgressIds, ...removeInProgressIds]));
    }, [[...editInProgressIds].sort().join(','), [...removeInProgressIds].sort().join(',')]);

    return {
        isAddInProgress: addActionState?.isProgress,
        inProgressIds,
    };
}
